import { createStandaloneToast, ToastId } from "@chakra-ui/toast";
import { NetworkMode, QueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import HttpStatusCode from "httpStatusCode";
import i18n from "i18n/config";
import { Toast } from "shared/components/toast/Toast";
import { UseToastProps } from "shared/components/toast/useToast";
import { isDev } from "shared/utils/DevUtils";
import { getErrorMessage } from "shared/utils/ErrorUtils";
import { theme } from "theme";

const networkMode: NetworkMode = isDev ? "always" : "online";

const { toast: openToast } = createStandaloneToast({ theme });

export const SILENT_STATUS_CODES = [HttpStatusCode.BAD_GATEWAY];

const isSilentStatusCode = (statusCode: HttpStatusCode) =>
  SILENT_STATUS_CODES.includes(statusCode);

const showToast = ({
  id,
  isClosable = true,
  ...props
}: UseToastProps): ToastId =>
  openToast({
    id,
    position: "bottom-right",
    render: ({ onClose }) => (
      <Toast
        isLoading={!isClosable}
        onClose={isClosable ? onClose : undefined}
        {...props}
      />
    ),
    duration: isClosable ? 4_000 : null,
  });

export const appQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      throwOnError: (e) => {
        const error = e as AxiosError;
        return !isSilentStatusCode(error.response?.status ?? 0);
      },
      retry: false,
      networkMode,

      // This was causing every suspending query to fire twice.
      // refetchOnMount: "always",

      // Don't cache query data.
      //
      // Fixes flickering from old to new data after page loads
      // and fixes stale data in forms (because defaultValues is only read once)
      //
      // This can't be 0 because it causes the same query to be fetched multiple times
      // when used in multiple components on the same page while the page is loading.
      gcTime: 10_000,
      staleTime: 10_000,
    },
    mutations: {
      networkMode,
      onError: (e) => {
        const error = e as AxiosError;
        // for now we will only handle forbidden errors through here
        if (
          error.response?.status == HttpStatusCode.FORBIDDEN &&
          !isSilentStatusCode(error.response.status)
        ) {
          showToast({
            message: getErrorMessage(i18n.t, error),
            variant: "error",
          });
        }
      },
    },
  },
});
